import { BehaviorSubject, Observable } from 'rxjs';

export interface TableState<F> {
  pageIndex: number;
  pageSize: number;
  searchTerm: string;
  filters: F;
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

export abstract class BaseTableStateService<F> {
  protected abstract getDefaultFilters(): F;

  private stateSubject: BehaviorSubject<TableState<F>>;
  public state$: Observable<TableState<F>>;

  private searchSubject = new BehaviorSubject<string>('');
  public search$ = this.searchSubject.asObservable();

  constructor() {
    const initialState: TableState<F> = {
      pageIndex: 1,
      pageSize: 10,
      searchTerm: '',
      filters: this.getDefaultFilters(),
      dateRange: {}
    };

    this.stateSubject = new BehaviorSubject<TableState<F>>(initialState);
    this.state$ = this.stateSubject.asObservable();
  }

  get snapshot(): TableState<F> {
    return this.stateSubject.value;
  }

  update(partial: Partial<TableState<F>>) {
    this.stateSubject.next({ ...this.snapshot, ...partial });
  }

  updateSearch(searchTerm: string) {
    this.stateSubject.next({
      ...this.snapshot,
      pageIndex: 1,
      searchTerm
    });

    this.searchSubject.next(searchTerm);
  }

  updateFilters(filters: Partial<F>) {
    this.stateSubject.next({
      ...this.snapshot,
      pageIndex: 1,
      filters: { ...this.snapshot.filters, ...filters }
    });
  }

  updateDateRange(from?: Date, to?: Date) {
    this.stateSubject.next({
      ...this.snapshot,
      pageIndex: 1,
      dateRange: { from, to }
    });
  }

  updatePagination(pageIndex: number, pageSize: number) {
    this.stateSubject.next({
      ...this.snapshot,
      pageIndex,
      pageSize
    });
  }

  reset() {
    this.stateSubject.next({
      pageIndex: 1,
      pageSize: 10,
      searchTerm: '',
      filters: this.getDefaultFilters(),
      dateRange: {}
    });
  }
}
